/**
 * SCM16 — Milestone & critical-path alert deriver.
 *
 * Given a `SupplyChainPlan` + the project COD, derives the list of BoM
 * items whose `expectedOnSiteDate` (or `expectedShipDate` proxy) implies
 * an arrival LATER than `requiredOnSiteDate`. The deriver expresses the
 * slip in months and tags the projected impact on the critical path so
 * the weekly job + AdminPanel inbox can render a human-readable banner:
 *
 *   "Transformer order expected ship 3 weeks late — critical path now -3 months."
 *
 * Pure: this module has zero IO. The Firestore writer + email dispatcher
 * live in `src/services/supplyChain/procurementAlertDispatcher.ts`.
 *
 * Derivation:
 *
 *   slipMonths = monthsBetween(expectedOnSiteDate, requiredOnSiteDate)
 *
 *   - When the BoM item has no `expectedOnSiteDate`, we synthesise one
 *     from `today + leadTimeMonthsHigh`. Conservative — matches what
 *     `criticalPathAnalysis` uses for slack.
 *   - When the BoM item has no `requiredOnSiteDate`, we synthesise one
 *     from `cod - installDuration - shipDuration` (defaults to `cod`
 *     directly when those overrides aren't provided).
 *   - When both fall back to defaults but `cod` is null, we suppress
 *     the alert (no anchor → no signal).
 *
 * Severity rules (tunable; pinned by regression tests):
 *   - critical : slipMonths ≥ 3 AND the item is on the critical path
 *   - high     : slipMonths ≥ 3 OR  (slipMonths ≥ 1 AND critical-path)
 *   - medium   : 0 < slipMonths < 1
 *   - none     : slipMonths ≤ 0 (no alert emitted)
 */
import type { BomItem, BomItemStatus, SupplyChainPlan } from '../types/supplyChain';
import {
  computeCriticalPath,
  type CriticalPathItem,
  type ComputeCriticalPathOptions,
} from './criticalPathAnalysis';

/** Conventional month length — kept aligned with `criticalPathAnalysis.MS_PER_MONTH`. */
const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

/** Severity bucket — surfaced as a coloured banner. */
export type ProcurementAlertSeverity = 'medium' | 'high' | 'critical';

/**
 * Single alert row, ready to be displayed inline OR persisted to the
 * Firestore inbox. `id` is deterministic (`alert-{siteId}-{bomItemId}-{slipBucket}`)
 * so re-running the deriver doesn't proliferate duplicate inbox rows.
 */
export interface ProcurementAlert {
  /** Deterministic id. */
  id: string;
  schemaVersion: 1;
  siteId: string;
  bomItemId: string;
  bomItemName: string;
  bomItemCategory: BomItem['category'];
  bomItemStatus: BomItemStatus;
  /** Months of slip — positive = late. */
  slipMonths: number;
  /** True when the underlying BoM item is on the project critical path. */
  isOnCriticalPath: boolean;
  /** Whether the slip is currently absorbed by slack
   *  (slipMonths > 0 but the path doesn't go negative). */
  consumesSlackOnly: boolean;
  /** Net projected schedule impact in months — negative = COD slipped. */
  projectedCodSlipMonths: number;
  severity: ProcurementAlertSeverity;
  /** Rendered banner sentence — locale-agnostic English. */
  message: string;
  /** ISO timestamp the alert was derived. */
  derivedAt: string;
}

export interface DeriveProcurementAlertsOptions extends ComputeCriticalPathOptions {
  /** Slack threshold (months) below which we promote an alert to `critical`.
   *  Defaults to 3 mo, matching `criticalPathAnalysis`. */
  criticalSlackThresholdMonths?: number;
}

export interface DeriveProcurementAlertsInput {
  plan: SupplyChainPlan | undefined;
  inServiceDate: string | null;
  siteId?: string;
  options?: DeriveProcurementAlertsOptions;
}

const DEFAULT_SITE_ID = 'unknown-site';

/**
 * Synthesise `expectedOnSiteDate` from `today + leadTimeMonthsHigh` when
 * the BoM item doesn't carry an explicit one. Returns null when the
 * item has no lead-time (truly unknown — no signal to emit).
 */
function synthesizeExpected(item: BomItem, now: Date): Date | null {
  if (item.expectedOnSiteDate) {
    const d = new Date(item.expectedOnSiteDate);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const leadMonths = item.leadTimeMonthsHigh;
  if (!Number.isFinite(leadMonths) || leadMonths <= 0) return null;
  return new Date(now.getTime() + leadMonths * MS_PER_MONTH);
}

/**
 * Synthesise `requiredOnSiteDate` from the COD anchor. Reserved for
 * future tightening — today we use COD directly when the item has no
 * explicit value.
 */
function synthesizeRequired(item: BomItem, cod: Date | null): Date | null {
  if (item.requiredOnSiteDate) {
    const d = new Date(item.requiredOnSiteDate);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return cod;
}

/**
 * Format a slip in months as a human banner string. Uses weeks for
 * sub-1-month slips so the wording stays natural (e.g. "3 weeks late").
 */
function formatSlipPhrase(slipMonths: number): string {
  if (slipMonths < 0.25) return 'on track';
  if (slipMonths < 1) {
    const weeks = Math.round(slipMonths * 4.345);
    return weeks <= 1 ? '1 week late' : `${weeks} weeks late`;
  }
  const months = slipMonths >= 12
    ? `${slipMonths.toFixed(1)} months`
    : `${Math.round(slipMonths)} months`;
  return `${months} late`;
}

function severityFor(slipMonths: number, isCritical: boolean): ProcurementAlertSeverity {
  if (isCritical && slipMonths >= 3) return 'critical';
  if (slipMonths >= 3) return 'high';
  if (isCritical && slipMonths >= 1) return 'high';
  return 'medium';
}

/**
 * Deterministic alert id — same (siteId, bomItemId, slipBucket) always
 * collapses to the same string so re-runs of the dispatcher don't
 * proliferate inbox rows. `slipBucket` is the integer month band so
 * a 3.2-mo slip and a 3.8-mo slip share the same row but a 3-mo and a
 * 6-mo slip create distinct entries.
 */
export function buildAlertId(siteId: string, bomItemId: string, slipMonths: number): string {
  const bucket = Math.max(0, Math.floor(slipMonths));
  return `alert-${siteId}-${bomItemId}-${bucket}mo`;
}

/**
 * Core derivation. Pure / deterministic given fixed `now`.
 *
 * Returns alerts sorted by severity desc → slip desc → name asc.
 */
export function deriveProcurementAlerts(
  input: DeriveProcurementAlertsInput,
): ProcurementAlert[] {
  const plan = input.plan;
  if (!plan || plan.bom.length === 0) return [];
  const inServiceDate = input.inServiceDate;
  const siteId = input.siteId ?? DEFAULT_SITE_ID;
  const opts = input.options ?? {};
  const nowRef = opts.now ? new Date(opts.now) : new Date();
  const nowIso = nowRef.toISOString();
  const cod = inServiceDate ? new Date(inServiceDate) : null;
  const codValid = cod !== null && !Number.isNaN(cod.getTime());

  // Critical-path index lets us cheaply look up isOnCriticalPath per item.
  const cpResult = computeCriticalPath(plan.bom, inServiceDate, opts);
  const cpById = new Map<string, CriticalPathItem>(
    cpResult.items.map((i) => [i.bomItemId, i]),
  );

  const alerts: ProcurementAlert[] = [];
  for (const item of plan.bom) {
    // Skip items that have already arrived / been commissioned — they
    // can't slip anymore.
    if (item.status === 'on-site' || item.status === 'commissioned') continue;
    const expected = synthesizeExpected(item, nowRef);
    const required = synthesizeRequired(item, codValid ? cod : null);
    if (!expected || !required) continue;
    const slipMs = expected.getTime() - required.getTime();
    if (slipMs <= 0) continue;
    const slipMonths = slipMs / MS_PER_MONTH;
    const cpEntry = cpById.get(item.id);
    const isCritical = cpEntry?.isOnCriticalPath ?? false;
    // When the item is on the critical path the slip directly hits COD.
    // When it's not, slack absorbs it up to the entry's positive slack.
    const slackMonthsPos = cpEntry && Number.isFinite(cpEntry.slackMonths)
      ? Math.max(0, cpEntry.slackMonths)
      : 0;
    const projectedCodSlip = -Math.max(0, slipMonths - slackMonthsPos);
    const consumesSlackOnly = projectedCodSlip === 0;
    const sev = severityFor(slipMonths, isCritical);
    const slipPhrase = formatSlipPhrase(slipMonths);
    const messageHead = `Expected ${slipPhrase}`;
    const messageTail = projectedCodSlip < 0
      ? ` — critical path now ${projectedCodSlip.toFixed(1)} months.`
      : ` — within slack, no COD impact yet.`;
    alerts.push({
      id: buildAlertId(siteId, item.id, slipMonths),
      schemaVersion: 1,
      siteId,
      bomItemId: item.id,
      bomItemName: item.name,
      bomItemCategory: item.category,
      bomItemStatus: item.status,
      slipMonths,
      isOnCriticalPath: isCritical,
      consumesSlackOnly,
      projectedCodSlipMonths: projectedCodSlip,
      severity: sev,
      message: messageHead + messageTail,
      derivedAt: nowIso,
    });
  }

  // Severity desc → slip desc → name asc.
  const sevOrder: Record<ProcurementAlertSeverity, number> = {
    critical: 3,
    high: 2,
    medium: 1,
  };
  alerts.sort((a, b) => {
    const s = sevOrder[b.severity] - sevOrder[a.severity];
    if (s !== 0) return s;
    if (b.slipMonths !== a.slipMonths) return b.slipMonths - a.slipMonths;
    return a.bomItemName.localeCompare(b.bomItemName);
  });

  return alerts;
}
